import React from "react";
import { toast } from "react-toastify";
import { NibiruTxClient, NibiruQuerier } from "@nibiruchain/nibijs";
import {
  setTxConfirmed,
  setTxFailed,
  setTxPending,
} from "../../ReduxSlices/ModalSlice";
import { setTokenUpdate } from "../../ReduxSlices/CommunicationSlice";
import { setEventToEmit } from "../../ReduxSlices/NotificationSlice";
import { api } from "./Api";
import { addToken, getIdFromHash } from "./Functions";
import { Testnet } from "@nibiruchain/nibijs";
import { setPerpsUpdate } from "../../ReduxSlices/TradeSlice";
import {
  evmToBech32Address,
  executeEvmContract,
} from "../../evm/services/EvmContract";
import { formatErrorForDisplay, getErrorIcon, getErrorColor } from "./ErrorHandler";

export const executeContract = async (
  emailData,
  funds,
  currentTime,
  chainId,
  endpt,
  contractAddr,
  dispatch,
  token_id,
  receiver,
  CONTRACT_MESSAGE,
  account,
  walletEx,
  tokenToSend
) => {
  try {
    toast.loading("Transaction is pending...");

    dispatch(setTxPending());
    await window[walletEx].enable(chainId);
    const signer = await window[walletEx].getOfflineSigner(chainId);
    const signingClient = await NibiruTxClient.connectWithSigner(endpt, signer);
    let hash;
    if (tokenToSend) {
      // 🚀 FIX: Properly convert amount to on-chain units (integer only)
      const usdcDecimals = Number(process.env.REACT_APP_USDC_DECIMALS) || 6;
      const humanReadableAmount = parseFloat(tokenToSend[0].amount);
      
      // Validate input amount
      if (!humanReadableAmount || humanReadableAmount <= 0 || isNaN(humanReadableAmount)) {
        throw new Error(`Invalid amount: ${tokenToSend[0].amount}. Amount must be greater than 0.`);
      }
      
      // Convert to on-chain units WITHOUT premature rounding
      // This preserves maximum precision during multiplication
      const onChainAmountFloat = humanReadableAmount * (10 ** usdcDecimals);
      
      // Round to nearest integer (not floor) to avoid losing value
      const onChainAmount = Math.round(onChainAmountFloat);
      
      // Validate final on-chain amount
      if (onChainAmount <= 0) {
        throw new Error(`Amount too small: ${humanReadableAmount} ${tokenToSend[0].denom}. Minimum amount is ${1 / (10 ** usdcDecimals)} ${tokenToSend[0].denom}.`);
      }
      
      console.log(`💰 Amount conversion: ${humanReadableAmount} → ${onChainAmount} (decimals: ${usdcDecimals})`);
      
      const tx = await signingClient.wasmClient.executeMultiple(
        account,
        [
          {
            contractAddress: contractAddr,
            msg: CONTRACT_MESSAGE,
            funds: [
              {
                amount: onChainAmount.toString(),
                denom: tokenToSend[0].denom,
              },
            ],
          },
        ],
        {
          amount: [{ denom: "unibi", amount: "500000" }], // 0.5 NIBI fee
          gas: "800000", // Higher gas limit for complex operations
        },
        "codedestate perp_trade"
      );
      hash = tx.transactionHash;
    } else {
      const tx = await signingClient.wasmClient.executeMultiple(
        account,
        [
          {
            contractAddress: contractAddr,
            msg: CONTRACT_MESSAGE,
          },
        ],
        {
          amount: [{ denom: "unibi", amount: "500000" }], // 0.5 NIBI fee
          gas: "800000", // Higher gas limit for complex operations like minting
        }
      );
      hash = tx.transactionHash;
    }
    
    dispatch(setTxConfirmed());
    toast.dismiss();
    toast.success("Transaction is confirmed!", {
      autoClose: 1000,
    });
    
    if (token_id) {
      if (hash) {
        // Always emit event for both sender and receiver
        dispatch(
          setEventToEmit({
            txHash: hash,
            action: Object.keys(CONTRACT_MESSAGE)[0],
            token_id: token_id,
            receiver: receiver,
            sender: account,
            timeUTC: currentTime instanceof Date ? currentTime.toISOString() : currentTime,
            funds: funds,
          })
        );
        
        // 📧 EMAIL VIA API: Send event to backend and handle email status
        api("user/addEventItem", {
          txHash: hash,
          action: Object.keys(CONTRACT_MESSAGE)[0],
          token_id: token_id,
          receiver: receiver,
          sender: account,
          timeUTC: currentTime instanceof Date ? currentTime.toISOString() : currentTime,
          funds: funds,
          emailData: emailData,
        })
        .then((response) => {
          // 📧 Handle email status feedback with user-friendly toasts
          if (response?.emailStatus) {
            const { travelerEmail, hostEmail } = response.emailStatus;
            const action = Object.keys(CONTRACT_MESSAGE)[0];
            
            // Get user-friendly action description
            const getActionDescription = (actionType) => {
              switch (actionType) {
                case "set_reservation_for_short_term":
                  return "Reservation confirmed";
                case "cancel_reservation_for_shortterm":
                  return "Reservation cancelled";
                case "cancel_rental_for_shortterm":
                  return "Rental cancelled";
                case "reject_reservation_for_shortterm":
                  return "Reservation rejected";
                case "finalize_short_term_rental":
                  return "Rental finalized";
                default:
                  return "Transaction completed";
              }
            };
            
            const actionDesc = getActionDescription(action);
            let successCount = 0;
            let errorCount = 0;
            
            // Track email status
            if (travelerEmail.sent) {
              successCount++;
              console.log(`✅ [EMAIL API] Email sent successfully to traveler: ${travelerEmail.email}`);
            } else if (travelerEmail.error) {
              errorCount++;
              console.error(`❌ [EMAIL API] Failed to send email to traveler: ${travelerEmail.error}`);
            }
            
            if (hostEmail.sent) {
              successCount++;
              console.log(`✅ [EMAIL API] Email sent successfully to host: ${hostEmail.email}`);
            } else if (hostEmail.error) {
              errorCount++;
              console.error(`❌ [EMAIL API] Failed to send email to host: ${hostEmail.error}`);
            }
            
            // Show user-friendly toast notifications
            if (successCount > 0 && errorCount === 0) {
              // All emails sent successfully
              if (successCount === 2) {
                toast.success(`📧 Email notifications sent! Traveler and host have been notified about ${actionDesc.toLowerCase()}.`, { 
                  autoClose: 4000,
                  position: "bottom-right"
                });
              } else if (travelerEmail.sent) {
                toast.success(`📧 Email notification sent! Traveler has been notified about ${actionDesc.toLowerCase()}.`, { 
                  autoClose: 4000,
                  position: "bottom-right"
                });
              } else if (hostEmail.sent) {
                toast.success(`📧 Email notification sent! Host has been notified about ${actionDesc.toLowerCase()}.`, { 
                  autoClose: 4000,
                  position: "bottom-right"
                });
              }
            } else if (successCount > 0 && errorCount > 0) {
              // Partial success
              toast.warning(`📧 Some email notifications were sent, but ${errorCount} failed. Transaction ${actionDesc.toLowerCase()} successfully.`, { 
                autoClose: 5000,
                position: "bottom-right"
              });
            } else if (errorCount > 0) {
              // All emails failed
              toast.error(`⚠️ Failed to send email notifications. Transaction ${actionDesc.toLowerCase()} successfully, but email delivery failed.`, { 
                autoClose: 5000,
                position: "bottom-right"
              });
            }
            // If both are null/undefined, no emails were attempted (which is fine)
          }
        })
        .catch(err => {
          console.error("❌ [EMAIL API] Failed to add event:", err);
          const action = Object.keys(CONTRACT_MESSAGE)[0];
          const actionDesc = action === "set_reservation_for_short_term" ? "reservation" :
                           action === "cancel_reservation_for_shortterm" ? "cancellation" :
                           action === "reject_reservation_for_shortterm" ? "rejection" :
                           "transaction";
          toast.error(`⚠️ Failed to send email notifications for ${actionDesc}. Transaction completed successfully.`, { 
            autoClose: 4000,
            position: "bottom-right"
          });
        });
      }
      
      // 🚀 OPTIMIZED: Only dispatch socket notification (no immediate RPC call)
      // Backend worker will handle property updates via blockchain indexer
      dispatch(
        setTokenUpdate({
          token_id: token_id,
          contract: contractAddr,
          sender: account,
        })
      );
      
      // 🚀 OPTIMIZED: Skip addToken RPC call - backend worker handles updates
      // This eliminates unnecessary RPC calls and re-renders
      // addToken(account, token_id, endpt, dispatch);
    }

    return hash;
  } catch (error) {
    dispatch(setTxFailed());

    const errorText = error.toString();
    console.error("Transaction error:", errorText);

    const isAlreadyApproved =
      errorText.includes("Approved already") ||
      errorText.includes("approved already") ||
      (errorText.includes("execute wasm contract failed") && errorText.includes("Approved"));

    if (isAlreadyApproved) {
      toast.dismiss();
      
      // Return a special indicator that the reservation is already approved
      return { alreadyApproved: true, error: errorText };
    }
    
    toast.dismiss();

    // Use the new error handling system
    const errorData = formatErrorForDisplay(errorText);
    const icon = getErrorIcon(errorData.category);
    const color = getErrorColor(errorData.category);

    // Show user-friendly error message
    toast.error(
      <div className="flex items-start gap-3">
        <div 
          className="w-6 h-6 rounded-full flex items-center justify-center text-sm flex-shrink-0 mt-0.5"
          style={{ backgroundColor: `${color}20`, color: color }}
        >
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm">Transaction Failed</div>
          <div className="text-sm mt-1">{errorData.message}</div>
        </div>
      </div>,
      {
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      }
    );

    // Log technical details for debugging
    console.error("Technical error details:", {
      category: errorData.category,
      originalError: errorText,
      userMessage: errorData.message
    });

    return false;
  }
};

/**
 * Unified rental execution helper.
 *
 * - If walletType is a Cosmos wallet (keplr / leap), delegates to executeContract (NibiruTxClient).
 * - If walletType is an EVM wallet (metamask / bybitWallet), uses executeEvmContract to call
 *   the CodedEstateEvmInterface gateway, then mirrors event emission and backend logging.
 */
export const executeRentalWithWallet = async ({
  emailData,
  funds,
  currentTime,
  chainId,
  endpt,
  contractAddr,
  dispatch,
  tokenId,
  receiver,
  CONTRACT_MESSAGE,
  account,
  walletType,
  walletEx,
  tokenToSend,
}) => {
  const isEvmWallet =
    walletType === "metamask" || walletType === "bybitWallet";

  // Cosmos wallet path: reuse existing executeContract behaviour as-is
  if (!isEvmWallet) {
    return executeContract(
      emailData,
      funds,
      currentTime,
      chainId,
      endpt,
      contractAddr,
      dispatch,
      tokenId,
      receiver,
      CONTRACT_MESSAGE,
      account,
      walletEx || walletType,
      tokenToSend
    );
  }

  // EVM wallet path: call the rental EVM gateway via executeEvmContract
  try {
    const hash = await executeEvmContract(
      dispatch,
      CONTRACT_MESSAGE,
      account,
      tokenToSend,
      funds
    );

    if (!hash) {
      return false;
    }

    // Mirror event emission and backend logging so downstream code stays unchanged.
    const action = Object.keys(CONTRACT_MESSAGE || {})[0] || "unknown";

    if (tokenId) {
      // Emit socket event for both sender and receiver
      const timeUTCSerialized = currentTime instanceof Date ? currentTime.toISOString() : currentTime;
      dispatch(
        setEventToEmit({
          txHash: hash,
          action,
          token_id: tokenId,
          receiver,
          sender: account,
          timeUTC: timeUTCSerialized,
          funds,
        })
      );

      // Persist event + email notifications via backend
      api("user/addEventItem", {
        txHash: hash,
        action,
        token_id: tokenId,
        receiver,
        sender: account,
        timeUTC: timeUTCSerialized,
        funds,
        emailData,
      }).catch((err) => {
        console.error("❌ [EMAIL API] Failed to add event (EVM path):", err);
      });

      // Trigger token sync pipeline (backend workers will handle blockchain indexing)
      dispatch(
        setTokenUpdate({
          token_id: tokenId,
          contract: contractAddr,
          sender: account,
        })
      );
    }

    return hash;
  } catch (error) {
    console.error("EVM rental execution failed:", error);
    // executeEvmContract already shows a toast; just surface failure to caller
    return false;
  }
};

export const executePerps = async (
  chainId,
  endpt,
  contractAddr,
  dispatch,
  CONTRACT_MESSAGE,
  account,
  walletEx,
  tokenToSend
) => {
  try {
    toast.loading("Transaction is pending...");
    dispatch(setTxPending());
    await window[walletEx].enable(chainId);
    const signer = await window[walletEx].getOfflineSigner(chainId);
    const signingClient = await NibiruTxClient.connectWithSigner(endpt, signer);
    let hash;

    if (tokenToSend) {
      const tx = await signingClient.wasmClient.executeMultiple(
        account,
        [
          {
            contractAddress: contractAddr,
            msg: CONTRACT_MESSAGE,
            funds: [
              {
                amount: tokenToSend[0].amount,
                denom: tokenToSend[0].denom,
              },
            ],
          },
        ],
        "auto",
        "perp_trade"
      );
      hash = tx.transactionHash;
    } else {
      const tx = await signingClient.wasmClient.execute(
        account,
        contractAddr,
        CONTRACT_MESSAGE,
        "auto",
        "",
        []
      );
      hash = tx.transactionHash;
    }

    console.log("Transaction successful! Hash:", hash);
    
    dispatch(setTxConfirmed());
    toast.dismiss();
    
    // Show transaction hash in success message with clickable link
    const explorerUrl = `http://explorer.nibiru.fi/nibiru-testnet-2/tx/${hash}`;
    
    toast.success(
      <div>
        Transaction confirmed! 
        <br />
        <a 
          href={explorerUrl} 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-blue-500 underline hover:text-blue-700"
        >
          View on Explorer
        </a>
      </div>,
      {
        autoClose: 10000,
        hideProgressBar: false,
      }
    );
    
    dispatch(setPerpsUpdate());
    
    // SMART EVENT EMISSION: Only trigger events for actual blockchain changes
    setTimeout(() => {
      // Emit user action event immediately
      window.dispatchEvent(new CustomEvent('userTradeAction', {
        detail: { 
          txHash: hash,
          contractMessage: CONTRACT_MESSAGE,
          account,
          timestamp: Date.now()
        }
      }));
      
      console.log('🔄 User trade action event emitted');
    }, 1000);

    // DELAYED STATE CHECK: Check for blockchain state changes after settlement
    setTimeout(() => {
      // This will trigger the smart detector to check for state changes
      window.dispatchEvent(new CustomEvent('blockchainStateCheck', {
        detail: { account, timestamp: Date.now() }
      }));
      
      console.log('Blockchain state check triggered');
    }, 5000);
    
    return hash;
  } catch (error) {
    console.error("Transaction failed:", error);
    dispatch(setTxFailed());
    toast.dismiss();

    // Use the new error handling system
    const errorData = formatErrorForDisplay(error.toString());
    const icon = getErrorIcon(errorData.category);
    const color = getErrorColor(errorData.category);

    // Show user-friendly error message
    toast.error(
      <div className="flex items-start gap-3">
        <div 
          className="w-6 h-6 rounded-full flex items-center justify-center text-sm flex-shrink-0 mt-0.5"
          style={{ backgroundColor: `${color}20`, color: color }}
        >
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm">Transaction Failed</div>
          <div className="text-sm mt-1">{errorData.message}</div>
        </div>
      </div>,
      {
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      }
    );

    // Log technical details for debugging
    console.error("Technical error details:", {
      category: errorData.category,
      originalError: error.toString(),
      userMessage: errorData.message
    });

    return false;
  }
};

export const queryContract = async (contractAddr, CONTRACT_MESSAGE, endpt) => {
  try {
    const querier = await NibiruQuerier.connect(endpt);
    const res = await querier.nibiruExtensions.wasm.queryContractSmart(
      contractAddr,
      CONTRACT_MESSAGE
    );
    
    return res;
  } catch (error) {
    console.log(error);
    // Don't show toast on error - only successful calls
  }
};

export const queryBalance = async (account, denom, endpt) => {
  try {
    const querier = await NibiruQuerier.connect(endpt);
    const balance = await querier.getBalance(account, denom);
    
    return balance;
  } catch (error) {
    console.log("Balance query error:", error);
    // Don't show toast on error - only successful calls
    throw error;
  }
};

const perpPriceCache = {
  data: null,
  timestamp: 0,
};

export const getPerpPrices = async (marketIndex) => {
  try {
    const now = Date.now();
    const CACHE_TTL = process.env.REACT_APP_PRICE_CACHE_TTL || 60000; // Default 1 minute

    // Return cached data if it's fresh
    if (perpPriceCache.data && now - perpPriceCache.timestamp < CACHE_TTL) {
      console.log("Using cached perp prices");
      return perpPriceCache.data;
    }

    console.log("Fetching fresh perp prices");
    const message = {
      get_perp_prices: {
        market_index: marketIndex, // Dubai market index
        collateral_index: process.env.REACT_APP_PERP_TOKEN_INDEXES_COLLATERAL, // USDC token index
      },
    };

    const result = await queryContract(
      process.env.REACT_APP_PERP_SMART_CONTRACT,
      message,
      Testnet(2).endptTm
    );

    if (result?.market) {
      console.log("Perp prices response:", result);
      const priceData = {
        basePrice: result.market.base_price_usd,
        basePerQuote: result.market.base_per_quote,
        collateralPrice: result.collateral,
        lastOracleUpdate: result.market.last_oracle_addr,
      };

      // Update cache
      perpPriceCache.data = priceData;
      perpPriceCache.timestamp = now;

      return priceData;
    }

    // If we have cache, return it even if expired - better than nothing
    if (perpPriceCache.data) {
      console.log("Contract query failed, using expired cache");
      return perpPriceCache.data;
    }

    return null;
  } catch (err) {
    console.error("Failed to get perp prices:", err);

    // Return cache on error if available
    if (perpPriceCache.data) {
      console.log("Error fetching prices, using cached data");
      return perpPriceCache.data;
    }

    return null;
  }
};

export const getCurrentPrice = async () => {
  try {
    const message = {
      get_exchange_rate: {
        base: 4,
        quote: 5,
      },
    };

    const result = await queryContract(
      process.env.REACT_APP_ORACLE_CONTRACT,
      message,
      Testnet(2).endptTm
    );

    return result?.base_price_usd || null;
  } catch (err) {
    console.error("Failed to fetch current price from oracle:", err);
    return null;
  }
};

/**
 * UNIVERSAL ADDRESS HANDLER - Converts EVM address to bech32 if needed
 * @param {string} account - Original account address (EVM or bech32)  
 * @param {string} walletType - Wallet type from useWalletConnection hook
 * @returns {Promise<string>} - Bech32 address for querying Cosmos contracts
 */
const getQueryableAddress = async (account, walletType) => {
  try {
    console.log("getQueryableAddress called with:", { account, walletType });
    
    if (!account) {
      console.warn("No account provided to getQueryableAddress");
      return null;
    }

    // Check if this is an EVM address
    const isEvmAddress = account.startsWith('0x');
    
    // Check if wallet type suggests EVM (even if null, we can detect by address format)
    const isEvmWallet = walletType === 'metamask' || walletType === 'bybitWallet' || isEvmAddress;
    
    console.log("Address analysis:", { 
      isEvmAddress, 
      isEvmWallet, 
      account, 
      walletType: walletType || 'auto-detected'
    });

    if (isEvmAddress) {
      console.log("EVM address detected - Converting to bech32...");
      const bech32Address = await evmToBech32Address(account);
      console.log("Address converted:", { 
        original: account, 
        bech32: bech32Address 
      });
      return bech32Address;
    } else if (account.startsWith('nibi')) {
      // Already a bech32 address (Cosmos wallets)
      console.log("Cosmos bech32 address - Using directly:", account);
      return account;
    } else {
      console.warn("Unknown address format, using as-is:", account);
      return account;
    }
  } catch (error) {
    console.error("Failed to get queryable address:", error);
    console.error("Error details:", {
      message: error.message,
      account,
      walletType
    });
    // Return original address as fallback
    return account;
  }
};

/**
 * Fetches all trades for a given account (handles EVM conversion automatically)
 * @param {string} account - Account address (EVM or bech32)
 * @param {string} walletType - Wallet type from useWalletConnection hook
 * @returns {Promise<Array>} Array of trades
 */
export const getTrades = async (account, walletType = null) => {
  try {
    
    if (!account) {
      console.warn("No account provided to getTrades");
      return [];
    }

    // Get the queryable address (converts EVM to bech32 if needed)
    const queryAddress = await getQueryableAddress(account, walletType);
    
    if (!queryAddress) {
      console.error("Failed to get valid query address");
      return [];
    }

    const message = {
      get_trades: {
        trader: queryAddress,
      },
    };

    const result = await queryContract(
      process.env.REACT_APP_PERP_SMART_CONTRACT,
      message,
      Testnet(2).endptTm
    );
    
    // Make sure we return the array from the result
    if (result && Array.isArray(result)) {
      return result;
    } else if (result && result.data && Array.isArray(result.data)) {
      return result.data;
    } else {
      console.warn("Unexpected trades result format:", result);
      return [];
    }
  } catch (err) {
    console.error("Failed to fetch trades:", err);
    console.error("Error details:", {
      message: err.message,
      stack: err.stack,
      account,
      walletType
    });
    return [];
  }
};

/**
 * Fetches all trade infos for a given account (handles EVM conversion automatically)
 * @param {string} account - Account address (EVM or bech32)
 * @param {string} walletType - Wallet type from useWalletConnection hook
 * @returns {Promise<Array>} Array of trade infos
 */
export const getTradeInfos = async (account, walletType = null) => {
  try {
    console.log("📊 getTradeInfos called with:", { account, walletType });
    
    if (!account) {
      console.warn("No account provided to getTradeInfos");
      return [];
    }

    // Get the queryable address (converts EVM to bech32 if needed)
    const queryAddress = await getQueryableAddress(account, walletType);
    
    if (!queryAddress) {
      console.error("Failed to get valid query address");
      return [];
    }

    console.log("🔍 Querying trade infos for converted address:", queryAddress);

    const message = {
      get_trade_infos: {
        trader: queryAddress, // Use converted address!
      },
    };

    const result = await queryContract(
      process.env.REACT_APP_PERP_SMART_CONTRACT,
      message,
      Testnet(2).endptTm
    );

    
    // Make sure we return the array from the result
    if (result && Array.isArray(result)) {
      return result;
    } else if (result && result.data && Array.isArray(result.data)) {
      return result.data;
    } else {
      console.warn("Unexpected trade infos result format:", result);
      return [];
    }
  } catch (err) {
    console.error("Failed to fetch trade infos:", err);
    console.error("Error details:", {
      message: err.message,
      stack: err.stack,
      account,
      walletType
    });
    return [];
  }
};

/**
 * Combines trades and tradeInfos into a single array (handles EVM conversion automatically)
 * @param {string} account - Account address (EVM or bech32)
 * @param {string} walletType - Wallet type from useWalletConnection hook
 * @returns {Promise<Array>} Array of combined trade objects
 */
export const getTradesWithInfos = async (account, walletType = null) => {
  try {
    
    if (!account) {
      console.warn("No account provided to getTradesWithInfos");
      return [];
    }

    // Execute both queries in parallel - both will use address conversion
    const [trades, tradeInfos] = await Promise.all([
      getTrades(account, walletType), // Passes walletType for conversion
      getTradeInfos(account, walletType), // Passes walletType for conversion
    ]);

    if (!Array.isArray(trades) || !Array.isArray(tradeInfos)) {
      console.warn("Invalid trades or tradeInfos data received");
      return [];
    }

    // Verify arrays have same length
    if (trades.length !== tradeInfos.length) {
      console.warn(
        `Trades (${trades.length}) and trade infos (${tradeInfos.length}) arrays have different lengths`
      );
    }

    // Combine the data - matching by index
    const combinedTrades = trades.map((trade, index) => {
      // Ensure we have a matching trade info
      const tradeInfo = index < tradeInfos.length ? tradeInfos[index] : {};

      return {
        ...trade,
        trade_info: tradeInfo,
      };
    });

    
    return combinedTrades;
  } catch (err) {
    console.error("❌ Failed to fetch combined trades and trade infos:", err);
    console.error("Error details:", {
      message: err.message,
      stack: err.stack,
      account,
      walletType
    });
    return [];
  }
};

/**
 * Get Open Interest for a specific market and collateral pair
 * @param {number} marketIndex - Market index (should be a number, not string)
 * @param {number} collateralIndex - Collateral index (defaults to 3 based on your config)
 * @returns {Promise<Object>} Open Interest data
 */
export const getOpenInterest = async (marketIndex, collateralIndex = 3) => {
  try {
    const message = {
      get_borrowing_pair_oi: {
        collateral_index: collateralIndex,
        market_index: marketIndex
      }
    };

    console.log("Open Interest query message:", message);

    const result = await queryContract(
      process.env.REACT_APP_PERP_SMART_CONTRACT,
      message,
      Testnet(2).endptTm
    );

    console.log("Open Interest result:", result);
    return result;
  } catch (error) {
    console.error("Failed to get open interest:", error);
    throw new Error(`Open Interest not available for market ${marketIndex} with collateral ${collateralIndex}`);
  }
};

/**
 * Get 1% Market Depth for price impact calculations
 * @param {number} marketIndex - Market index
 * @returns {Promise<Object>} Market depth data
 */
export const getMarketDepth = async (marketIndex) => {
  try {
    const message = {
      get_pair_depth: {
        index: marketIndex
      }
    };

    console.log("Market Depth query message:", message);

    const result = await queryContract(
      process.env.REACT_APP_PERP_SMART_CONTRACT,
      message,
      Testnet(2).endptTm
    );

    console.log("Market Depth result:", result);
    return result;
  } catch (error) {
    console.error("Failed to get market depth:", error);
    throw new Error(`Market depth not available for market ${marketIndex}`);
  }
};

/**
 * Get all prices for a market (including collateral price for USD conversion)
 * @param {number} marketIndex - Market index
 * @param {number} collateralIndex - Collateral index (defaults to 3)
 * @returns {Promise<Object>} Price data
 */
export const getPerpPricesForImpact = async (marketIndex, collateralIndex = 3) => {
  try {
    const message = {
      get_perp_prices: {
        market_index: `MarketIndex(${marketIndex})`,
        collateral_index: `TokenIndex(${collateralIndex})`
      }
    };

    console.log("Perp Prices query message:", message);

    const result = await queryContract(
      process.env.REACT_APP_PERP_SMART_CONTRACT,
      message,
      Testnet(2).endptTm
    );

    console.log("Perp Prices for Impact result:", result);
    return result;
  } catch (error) {
    console.error("Failed to get perp prices for impact:", error);
    throw new Error(`Price data not available for market ${marketIndex}`);
  }
};

/**
 * Calculate price impact for a trade
 * @param {number} marketIndex - Market index
 * @param {number} collateralIndex - Collateral index
 * @param {number} tradeSize - Trade size in USD
 * @param {boolean} isLong - Whether the trade is long
 * @param {number} entryPrice - Entry price in USD
 * @returns {Promise<Object>} Price impact calculation result
 */
export const calculatePriceImpact = async (marketIndex, collateralIndex, tradeSize, isLong, entryPrice) => {
  try {
    console.log("Calculating price impact for:", { marketIndex, collateralIndex, tradeSize, isLong, entryPrice });

    // Check if we have valid inputs
    if (!marketIndex && marketIndex !== 0) {
      throw new Error("Market index is required");
    }
    if (!tradeSize || tradeSize <= 0) {
      throw new Error("Trade size must be greater than 0");
    }
    if (!entryPrice || entryPrice <= 0) {
      throw new Error("Entry price must be greater than 0");
    }

    // Fetch all required data in parallel
    const [openInterestData, marketDepthData, priceData] = await Promise.all([
      getOpenInterest(marketIndex, collateralIndex),
      getMarketDepth(marketIndex),
      getPerpPricesForImpact(marketIndex, collateralIndex)
    ]);

    // Validate all data was received
    if (!openInterestData) {
      throw new Error("Failed to fetch open interest data");
    }
    if (!marketDepthData) {
      throw new Error("Failed to fetch market depth data");
    }
    if (!priceData) {
      throw new Error("Failed to fetch price data");
    }

    // Extract and validate collateral price
    const collateralPrice = parseFloat(priceData.collateral);
    if (!collateralPrice || collateralPrice <= 0) {
      throw new Error("Invalid collateral price received");
    }

    // Extract and validate open interest values
    const longOI = parseFloat(openInterestData.long || "0");
    const shortOI = parseFloat(openInterestData.short || "0");
    
    // Convert Open Interest from token units to USD
    const openInterestUsd = (longOI + shortOI) * collateralPrice;

    // Calculate adjusted OI
    const adjustedOI = openInterestUsd + (tradeSize / 2);

    // Extract market depth based on position direction
    const depthAbove = parseFloat(marketDepthData.one_percent_depth_above_usd || "0");
    const depthBelow = parseFloat(marketDepthData.one_percent_depth_below_usd || "0");
    
    const depth = isLong ? depthAbove : depthBelow;

    if (depth === 0) {
      console.log("Market depth is zero - applying no price impact");
      const result = {
        impactUsd: 0,
        impactPercent: 0,
        finalPrice: entryPrice,
        openInterestUsd: openInterestUsd,
        adjustedOI: adjustedOI,
        depth: depth,
        collateralPrice: collateralPrice
      };

      console.log("Price impact calculation result (zero depth):", result);

      return {
        success: true,
        data: result
      };
    }

    // Calculate price impact percentage (as decimal, not percentage)
    const impactPercent = adjustedOI / depth / 100;

    // Calculate price impact in USD
    const priceImpact = entryPrice * impactPercent;

    // Calculate final execution price
    const finalPrice = isLong ? entryPrice + priceImpact : entryPrice - priceImpact;

    const result = {
      impactUsd: priceImpact,
      impactPercent: impactPercent * 100, // Convert to percentage for display
      finalPrice: finalPrice,
      openInterestUsd: openInterestUsd,
      adjustedOI: adjustedOI,
      depth: depth,
      collateralPrice: collateralPrice
    };

    console.log("Price impact calculation result:", result);

    return {
      success: true,
      data: result
    };

  } catch (error) {
    console.error("Price impact calculation failed:", error);
    return {
      success: false,
      error: error.message
    };
  }
};